
import React, { useEffect, useRef } from 'react';

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  radius: number;
  trail: { x: number, y: number, age: number }[];
}

interface TopologicalLine {
  startX: number;
  startY: number;
  endX: number;
  endY: number;
  opacity: number;
  direction: number; // 1 for fading in, -1 for fading out
  life: number;
}

interface BrownianMotionProps {
  className?: string;
  particleCount?: number;
  speed?: number;
  trailLength?: number;
  color?: string;
}

const BrownianMotion = ({
  className = "",
  particleCount = 3,
  speed = 0.5,
  trailLength = 50,
  color = "#ea384c"
}: BrownianMotionProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const particlesRef = useRef<Particle[]>([]);
  const topoLinesRef = useRef<TopologicalLine[]>([]);
  const animationFrameRef = useRef<number>(0);
  const lastUpdateTimeRef = useRef<number>(Date.now());

  // Initialize particles
  const initParticles = (width: number, height: number) => {
    const particles: Particle[] = [];
    
    for (let i = 0; i < particleCount; i++) {
      particles.push({
        x: Math.random() * width,
        y: Math.random() * height,
        vx: (Math.random() - 0.5) * speed,
        vy: (Math.random() - 0.5) * speed,
        radius: 3 + Math.random() * 2,
        trail: [],
      });
    }
    
    return particles;
  };

  const createTopologicalLine = (width: number, height: number) => {
    // Create a new topological line at a random position
    const startX = Math.random() * width;
    const startY = Math.random() * height;
    const angle = Math.random() * Math.PI * 2;
    const length = 100 + Math.random() * 150;
    
    return {
      startX,
      startY,
      endX: startX + Math.cos(angle) * length,
      endY: startY + Math.sin(angle) * length,
      opacity: 0,
      direction: 1, // Start by fading in
      life: Math.random() * 100 + 100, // Random lifespan
    };
  };

  const updateParticles = (width: number, height: number, deltaTime: number) => {
    const timeScale = deltaTime / 16.6667; // Normalize based on 60fps
    
    return particlesRef.current.map(particle => {
      // Random direction changes (Brownian motion)
      particle.vx += (Math.random() - 0.5) * 0.05 * timeScale;
      particle.vy += (Math.random() - 0.5) * 0.05 * timeScale;
      
      // Limit velocity
      const maxSpeed = speed * 0.8;
      const currentSpeed = Math.sqrt(particle.vx * particle.vx + particle.vy * particle.vy);
      if (currentSpeed > maxSpeed) {
        particle.vx = (particle.vx / currentSpeed) * maxSpeed;
        particle.vy = (particle.vy / currentSpeed) * maxSpeed;
      }
      
      // Update position
      let newX = particle.x + particle.vx * timeScale;
      let newY = particle.y + particle.vy * timeScale;
      
      // Boundary checking (bounce off edges)
      if (newX < 0 || newX > width) {
        particle.vx *= -1;
        newX = Math.max(0, Math.min(width, newX));
      }
      if (newY < 0 || newY > height) {
        particle.vy *= -1;
        newY = Math.max(0, Math.min(height, newY));
      }
      
      // Add to trail
      particle.trail.unshift({ x: particle.x, y: particle.y, age: 0 });
      
      // Update trail point ages and remove old ones
      // Enhanced: Increased maxAge for longer lasting trails to improve the fading effect
      const maxAge = 300; // Further increased from 240 to 300 for longer trails
      particle.trail = particle.trail
        .map(point => ({ ...point, age: point.age + 1 }))
        .filter(point => point.age < maxAge);
      
      return {
        ...particle,
        x: newX,
        y: newY,
      };
    });
  };

  const updateTopologicalLines = (width: number, height: number, deltaTime: number) => {
    // Chance to add a new line
    if (Math.random() < 0.01 * deltaTime / 16.6667 && topoLinesRef.current.length < 20) {
      topoLinesRef.current.push(createTopologicalLine(width, height));
    }

    // Update existing lines
    topoLinesRef.current = topoLinesRef.current.filter(line => {
      // Update opacity based on direction
      line.opacity += line.direction * 0.005 * deltaTime / 16.6667;
      
      // Change direction when reaching limits
      if (line.opacity >= 0.4) {
        line.direction = -1; // Start fading out
      }
      
      line.life -= deltaTime / 16.6667;
      
      // Remove completely faded out lines or those that have lived their life
      return line.opacity > 0 && line.life > 0;
    });

    return topoLinesRef.current;
  };

  const drawParticles = (ctx: CanvasRenderingContext2D) => {
    ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);

    // Draw topological lines first (so they appear behind particles)
    topoLinesRef.current.forEach(line => {
      ctx.beginPath();
      ctx.moveTo(line.startX, line.startY);
      ctx.lineTo(line.endX, line.endY);
      
      // Use black color for topological lines with varying opacity
      ctx.strokeStyle = `rgba(0, 0, 0, ${line.opacity})`;
      ctx.lineWidth = 0.5;
      ctx.stroke();
    });
    
    particlesRef.current.forEach(particle => {
      // Enhanced: Improved trail with gradient fading effect
      if (particle.trail.length > 1) {
        for (let i = 0; i < particle.trail.length - 1; i++) {
          const point = particle.trail[i];
          const nextPoint = particle.trail[i + 1];
          
          // Enhanced: Calculate opacity based on age with a more dramatic fade
          const maxAge = 300; // Match the maxAge from updateParticles
          const opacity = Math.max(0, 1 - Math.pow(point.age / maxAge, 1.5)); // Enhanced exponential fade
          
          ctx.beginPath();
          ctx.moveTo(point.x, point.y);
          ctx.lineTo(nextPoint.x, nextPoint.y);
          
          // Parse the color to enable rgba with opacity
          const r = parseInt(color.slice(1, 3), 16);
          const g = parseInt(color.slice(3, 5), 16);
          const b = parseInt(color.slice(5, 7), 16);
          ctx.strokeStyle = `rgba(${r}, ${g}, ${b}, ${opacity})`;
          
          // Enhanced: Line width diminishes with age too, more gradually
          const widthFactor = Math.max(0.2, 1 - (i / particle.trail.length) * 0.8);
          ctx.lineWidth = particle.radius * widthFactor * Math.max(0.2, opacity);
          ctx.stroke();
        }
      }
      
      // Draw particle
      ctx.beginPath();
      ctx.arc(particle.x, particle.y, particle.radius, 0, Math.PI * 2);
      ctx.fillStyle = color;
      ctx.fill();
      
      // Add subtle glow effect
      const glowRadius = particle.radius * 2;
      const glowGradient = ctx.createRadialGradient(
        particle.x, particle.y, particle.radius * 0.5,
        particle.x, particle.y, glowRadius
      );
      glowGradient.addColorStop(0, `rgba(${parseInt(color.slice(1, 3), 16)}, ${parseInt(color.slice(3, 5), 16)}, ${parseInt(color.slice(5, 7), 16)}, 0.6)`);
      glowGradient.addColorStop(1, `rgba(${parseInt(color.slice(1, 3), 16)}, ${parseInt(color.slice(3, 5), 16)}, ${parseInt(color.slice(5, 7), 16)}, 0)`);
      
      ctx.beginPath();
      ctx.arc(particle.x, particle.y, glowRadius, 0, Math.PI * 2);
      ctx.fillStyle = glowGradient;
      ctx.fill();
    });
  };

  const animate = () => {
    if (!canvasRef.current) return;
    
    const now = Date.now();
    const deltaTime = now - lastUpdateTimeRef.current;
    lastUpdateTimeRef.current = now;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    if (ctx) {
      particlesRef.current = updateParticles(canvas.width, canvas.height, deltaTime);
      topoLinesRef.current = updateTopologicalLines(canvas.width, canvas.height, deltaTime);
      drawParticles(ctx);
    }
    
    animationFrameRef.current = requestAnimationFrame(animate);
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    // Set canvas size
    const resize = () => {
      const containerRect = canvas.parentElement?.getBoundingClientRect();
      if (!containerRect) return;
      
      canvas.width = containerRect.width;
      canvas.height = containerRect.height;
      
      // Reinitialize particles when resized
      particlesRef.current = initParticles(canvas.width, canvas.height);
      topoLinesRef.current = []; // Clear topological lines on resize
      
      // Initialize some topological lines
      for (let i = 0; i < 5; i++) {
        topoLinesRef.current.push(createTopologicalLine(canvas.width, canvas.height));
      }
    };
    
    resize();
    window.addEventListener('resize', resize);
    
    // Start animation
    particlesRef.current = initParticles(canvas.width, canvas.height);
    lastUpdateTimeRef.current = Date.now();
    animationFrameRef.current = requestAnimationFrame(animate);
    
    // Cleanup
    return () => {
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(animationFrameRef.current);
    };
  }, []);

  return (
    <canvas 
      ref={canvasRef}
      className={`absolute inset-0 pointer-events-none z-0 ${className}`}
    />
  );
};

export default BrownianMotion;
